/**
 * @file tableAction.service.ts
 * @description Operational services for moving, merging, transferring, and requesting bills.
 */

import { supabase } from '../../../config/supabase.config';

/**
 * Moves an open order from one table to another.
 */
export const moveTableOrder = async (
  orderId: string,
  destinationTableId: string,
  destinationTableNumber: string
): Promise<void> => {
  const { error } = await supabase
    .from('orders')
    .update({
      table_id: destinationTableId,
      metadata: {
        table_number: destinationTableNumber,
      },
    })
    .eq('id', orderId);

  if (error) {
    console.error('[tableAction.service] Move table error:', error);
    throw error;
  }
};

/**
 * Transfers server ownership of a table order to another waiter.
 */
export const transferTableWaiter = async (
  orderId: string,
  newWaiterId: string,
  newWaiterName: string
): Promise<void> => {
  const { error } = await supabase
    .from('orders')
    .update({
      waiter_id: newWaiterId,
      metadata: {
        waiter_name: newWaiterName,
      },
    })
    .eq('id', orderId);

  if (error) {
    console.error('[tableAction.service] Transfer waiter error:', error);
    throw error;
  }
};

/**
 * Flags an order as having requested the bill.
 */
export const requestTableBill = async (
  orderId: string,
  venueId: string,
  tableId: string
): Promise<void> => {
  // 1. Mark order timestamp
  const { error: orderErr } = await supabase
    .from('orders')
    .update({ bill_requested_at: new Date().toISOString() })
    .eq('id', orderId);

  if (orderErr) throw orderErr;

  // 2. Insert waiter request record for cashier / manager alerts
  await supabase
    .from('waiter_requests')
    .insert({
      venue_id: venueId,
      table_id: tableId,
      type: 'bill',
      status: 'pending',
    });
};

/**
 * Voids / cancels an entire table order and associated pending items and requests.
 */
export const cancelTableOrder = async (
  orderId: string,
  reason: string = 'Staff voided order'
): Promise<void> => {
  const now = new Date().toISOString();

  // 1. Fetch current order metadata to preserve existing fields
  const { data: existing } = await supabase
    .from('orders')
    .select('metadata')
    .eq('id', orderId)
    .maybeSingle();

  const currentMeta = (existing?.metadata && typeof existing.metadata === 'object') ? existing.metadata : {};
  const updatedMeta = {
    ...currentMeta,
    cancelled_at: now,
    cancelled_reason: reason,
  };

  // 2. Mark order as cancelled
  const { error: orderError } = await supabase
    .from('orders')
    .update({
      status: 'cancelled',
      metadata: updatedMeta,
    })
    .eq('id', orderId);

  if (orderError) {
    console.error('[tableAction.service] Cancel order error:', orderError);
    throw orderError;
  }

  // 3. Mark active line items as cancelled
  await supabase
    .from('order_items')
    .update({
      status: 'cancelled',
      cancelled_reason: reason,
    })
    .eq('order_id', orderId)
    .in('status', ['pending', 'sent_to_kitchen', 'preparing']);

  // 4. Mark associated pending waiter requests as completed
  await supabase
    .from('waiter_requests')
    .update({ status: 'completed' })
    .eq('metadata->>order_id', orderId)
    .eq('status', 'pending');
};
